<template>
  <button class="ai-header-button"
          tabindex="0"
          type="button"
          aria-label="Ask Expert AI"
          :class="{ 'visible-search-bar': searchBarHeader,
                    'visible-login-register-buttons': loginRegisterButtons,
                    'is-mobile': isMobile,
                    'is-cobranding': showCoBranding }"
          @click="handleAction">
    <div class="ai-header-icon-container">
      <img :src="aiHeaderIconSrc" class="ai-header-icon" alt="ai-header-icon">
    </div>
    <div class="ai-header-button-text-container">
      <div class="ai-header-button-text text-normal">
        {{ $t("HEADER.ASK_EXPERT_AI") }}
      </div>
      <div class="ai-header-button-text text-small">
        {{ $t("HEADER.ASK_EXPERT_AI_SHORT") }}
      </div>
    </div>
  </button>
</template>

<script setup>
import { useStore } from 'vuex';
import { computed } from 'vue';
import useExpertAiAction from '_acaSrc/composables/useExpertAiAction.composable';
import aiSparkMobileIcon from '~_acaAssets/utd-ai/utd-ai-spark-mobile.svg';
import aiSparkDesktopIcon from '~_acaAssets/utd-ai/utd-ai-spark.svg';

defineProps({
    searchBarHeader: {
        type: Boolean,
        default: false
    },
    loginRegisterButtons: {
        type: Boolean,
        default: false
    },
    isMobile: {
        type: Boolean,
        default: false
    }
});

const store = useStore();
const showCoBranding = computed(() => store.getters['app/showCoBranding']);
const isDesktopView = computed(() => store.getters['device/isDesktopView']);
const aiHeaderIconSrc = computed(() => {
    return isDesktopView.value ? aiSparkDesktopIcon : aiSparkMobileIcon;
});
const { handleExpertAiAction } = useExpertAiAction();

const handleAction = async(event) => {
    const uiElementName = 'ai-header-btn';
    await handleExpertAiAction({
        event,
        uiElementName
    });
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/wkce-app-spacing.less';
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

/* stylelint-disable declaration-property-value-no-unknown */

.icon-only() {
  .ds1-pa-0();
  width: 32px;
  height: 32px;
  border-radius: 50%;
  justify-content: center;

  .ai-header-button-text-container {
    display: none;
  }
}

.text-small() {
  &.text-normal {
    display: none;
  }

  &.text-small {
    display: block;
  }
}

.text-normal() {
  &.text-normal {
    display: block;
  }

  &.text-small {
    display: none;
  }
}

.ai-header-button {
  margin-top: 12px;
  padding: 8px 11px;
  height: 32px;
  background: none;
  border-radius: 16px;
  border: 1px solid @WKCE-CREAM-TINT1;
  display: flex;
  align-items: center;
  gap: 8px;

  .ai-header-icon-container {
    display: flex;
    align-items: center;

    .ai-header-icon-container {
      display: flex;
      align-items: center;
    }
  }

  .ai-header-button-text-container {
    display: flex;
    align-items: flex-start;

    .ai-header-button-text {
      .text-normal();
      color: @WKCE-WHITE;
      font-feature-settings: 'case' on, 'cpsp' on, 'dlig' on, 'mgrk' on, 'liga' off, 'clig' off;
      font-family: "Fira Sans", sans-serif;
      font-size: 18px;
    }
  }

  &:not(.visible-login-register-buttons) {
    .ds1-ml-2();
  }

  &.is-mobile.visible-search-bar {
    .icon-only();
    .ds1-ml-0();
    position: absolute;
    left: 44px;
  }

  &:focus-visible {
    border: 2px solid @WKCE-WHITE;
  }

  &:hover {
    background-color: @WKCE-BLUE-SHADE1;
  }

  &:active {
    background-color: @WKCE-BLUE;
  }
}

/* stylelint-disable-next-line order/order */
@media (max-width: 430px) {
  .ai-header-button {
    .icon-only();

    &:not(.visible-login-register-buttons) {
      .ds1-ml-1();
    }
  }
}

@media (min-width: 768px) {
  .ai-header-button {
    &.visible-login-register-buttons {
      margin-top: 7px;
    }
  }

  .isDesktop {
    .ai-header-button {
      .ds1-ml-2();
      margin-top: 10px;
      padding: 8px 11px;
      height: 34px;

      .ai-header-button-text-container {
        .ai-header-button-text {
          color: @WKCE-GRAY-SHADE2;
        }
      }

      &.visible-search-bar,
      &:not(.visible-search-bar).is-cobranding {
        .ai-header-button-text-container {
          .ai-header-button-text {
            .text-small();
          }
        }
      }

      &:focus-visible {
        border: 2px solid @WKCE-BLACK;
      }

      &:hover {
        background-color: @WKCE-CREAM-TINT3;
      }

      &:active {
        background-color: @WKCE-CREAM-TINT1;
      }
    }
  }
}

@media (min-width: 1070px) {
  .isDesktop {
    .ai-header-button {
      .ds1-ml-4();

      &:not(.visible-search-bar).is-cobranding {
        .ai-header-button-text-container {
          .ai-header-button-text {
            .text-normal();
          }
        }
      }
    }
  }
}

@media (min-width: 1440px) {
  .isDesktop {
    .ai-header-button {
      &.visible-search-bar {
        .ai-header-button-text-container .ai-header-button-text {
          .text-normal();
        }
      }
    }
  }
}
</style>